import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';

import Modal from './Modal';
import ThemeContext from '../../theme/ThemeContext';
import ThemeIcons from '../../theme/Icons';
import { convertTimestampToPickerDate, convertPickerDateToTimestamp } from '../../lib/dateTimeHelper';

import 'react-datepicker/dist/react-datepicker.css';

const StyledDatePickerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  .react-datepicker-wrapper {
    width: 100%;
  }
  
  .react-datepicker__input-container {
    width: 100%;
  }
  
  .react-datepicker__input-container input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid ${(props) => props.theme.borderColor};
    border-radius: ${(props) => props.theme.borderRadius}px;
    font-size: 14px;
    font-family: ${(props) => props.theme.fontFamily};
    background-color: ${(props) => props.theme.backgroundColor};
    color: ${(props) => props.theme.fontColorPrimary};
    
    &:focus {
      outline: none;
      border-color: ${(props) => props.theme.hoverColor};
    }
  }

  .react-datepicker__day-name {
    font-weight: 600;
  }

  .react-datepicker__day--outside-month {
    opacity: 0.5;
  }
  
  .react-datepicker {
    border: 1px solid ${(props) => props.theme.borderColor};
    border-radius: ${(props) => props.theme.borderRadius}px;
    font-family: ${(props) => props.theme.fontFamily};
    background-color: ${(props) => props.theme.backgroundColor};
  }
  
  .react-datepicker__header  {
    background-color: ${(props) => props.theme.backgroundColor};
    border-bottom: 1px solid ${(props) => props.theme.borderColor};
  }
  
  .react-datepicker__current-month,
  .react-datepicker-time__header,
  .react-datepicker__day-name {
    color: ${(props) => props.theme.fontColorPrimary};
  }
  
  .react-datepicker__day {
    color: ${(props) => props.theme.fontColorPrimary};

    &.react-datepicker__day--keyboard-selected {
      background-color: transparent;
    }
    
    &:hover {
      background-color: ${(props) => props.theme.primaryColor};
      color: ${(props) => props.theme.backgroundColor};
      border-radius: ${(props) => props.theme.borderRadius}px;
    }
    
    &.react-datepicker__day--selected {
      background-color: ${(props) => props.theme.primaryColor};
      color: ${(props) => props.theme.backgroundColor};
      border-radius: ${(props) => props.theme.borderRadius}px;
    }
    
    &.react-datepicker__day--today {
      background-color: ${(props) => props.theme.borderColor};
      border: 1px solid ${(props) => props.theme.borderColor};
      border-radius: ${(props) => props.theme.borderRadius}px;

    }
  }
  
  .react-datepicker__time-container {
    border-left: 1px solid ${(props) => props.theme.borderColor};
  }
  
  .react-datepicker__time {
    background-color: ${(props) => props.theme.backgroundColor};

    .react-datepicker__time-list-item.react-datepicker__time-list-item--selected {
      background-color: ${(props) => props.theme.primaryColor} !important;
      color: ${(props) => props.theme.backgroundColor} !important;
    }

    .react-datepicker__time-list-item {
      color: ${(props) => props.theme.fontColorPrimary};
      
      &:hover {
        background-color: ${(props) => props.theme.primaryColor} !important;
        color: ${(props) => props.theme.backgroundColor} !important;
      }
      
      &.react-datepicker__time-list-item--selected {
        background-color: ${(props) => props.theme.primaryColor};
        color: ${(props) => props.theme.backgroundColor};
      }
    }
  }
  .react-datepicker__current-month {
    display: none;
  }
  
  .react-datepicker__navigation {
    display: none;
  }
  

`;


/**
 * Detects the picker type based on the day.js format string
 * @param {string} format - Day.js format string
 * @returns {string} - 'date', 'time', or 'datetime'
 */
const detectPickerType = (format) => {
  if (!format || typeof format !== 'string') {
    return 'date'; // Default to date picker
  }
  const hasDate = /[YMD]/.test(format);
  const hasTime = /[hHmsa]/.test(format);

  if (hasDate && hasTime) return 'datetime';
  if (hasTime) return 'time';
  return 'date';
};

/**
 * Detects if the format uses 12-hour (AM/PM) or 24-hour time
 * Based on Unicode Technical Standard #35 and day.js documentation:
 * - Lowercase 'h' or 'hh' = 12-hour format
 * - Uppercase 'H' or 'HH' = 24-hour format
 * @param {string} format - Day.js format string
 * @returns {boolean} - true for 12-hour format, false for 24-hour format
 */
const is12HourFormat = (format) => {
  if (!format || typeof format !== 'string') {
    return false;
  }
  return /h/.test(format);
};

/**
 * Converts day.js format to react-datepicker format
 * @param {string} dayjsFormat - Day.js format string
 * @returns {string} - React-datepicker format string
 */
const convertDayjsFormatToDatePickerFormat = (dayjsFormat) => {
  if (!dayjsFormat || typeof dayjsFormat !== 'string') {
    return 'MM/dd/yyyy'; // Default format
  }
  return dayjsFormat
    .replace(/YYYY/g, 'yyyy')
    .replace(/MM/g, 'MM')
    .replace(/DD/g, 'dd')
    .replace(/hh/g, 'hh')
    .replace(/mm/g, 'mm')
    .replace(/ss/g, 'ss')
    .replace(/A/g, 'aa');
};

const DateTimePickerModal = ({
  isOpen,
  value = null,
  format,
  onSubmit,
  onClose,
  onClear,
  timeIntervals = 1,
  allowKeyboardInput = true,
  timezone
}) => {
  const theme = useContext(ThemeContext);
  const [selectedDate, setSelectedDate] = useState(() => {
    if (value && typeof value === 'number') {
      return convertTimestampToPickerDate(value, timezone, format);
    }
    return new Date();
  });

  const pickerType = detectPickerType(format);
  const datePickerFormat = convertDayjsFormatToDatePickerFormat(format);
  const use12HourFormat = is12HourFormat(format);

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleSubmit = () => {
    if (selectedDate && onSubmit) {
      const timestamp = convertPickerDateToTimestamp(selectedDate, timezone, format);
      onSubmit(timestamp);
      onClose();
    }
  };

  const handleClear = () => {
    if (onClear) {
      onClear();
      onClose();
    }
  };


  const renderDatePicker = () => {
    const commonProps = {
      selected: selectedDate,
      onChange: handleDateChange,
      dateFormat: datePickerFormat,
      showPopperArrow: false,
      inline: true,
      allowSameDay: true,
      allowKeyboardNavigation: allowKeyboardInput,
      enableTabLoop: false,
      showYearDropdown: true,
      showMonthDropdown: true,
      dropdownMode: "select",
    };


    switch (pickerType) {
      case 'date':
        return (
          <DatePicker
            {...commonProps}
            showTimeSelect={false}
            showTimeSelectOnly={false}
          />
        );

      case 'time':
        return (
          <DatePicker
            {...commonProps}
            showTimeSelect={true}
            showTimeSelectOnly={true}
            timeFormat={use12HourFormat ? "hh:mm aa" : "HH:mm"}
            timeIntervals={timeIntervals}
            timeCaption="Time"
          />
        );

      case 'datetime':
        return (
          <DatePicker
            {...commonProps}
            showTimeSelect={true}
            showTimeSelectOnly={false}
            timeFormat={use12HourFormat ? "hh:mm aa" : "HH:mm"}
            timeIntervals={timeIntervals}
            timeCaption="Time"
            dateFormat={`${datePickerFormat} ${use12HourFormat ? "hh:mm aa" : "HH:mm"}`}
          />
        );

      default:
        return (
          <DatePicker
            {...commonProps}
            showTimeSelect={false}
            showTimeSelectOnly={false}
          />
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onHide={onClose}
      title="Select Date/Time"
      icon={ThemeIcons.misc.calendar}
      width={360}
      height={360}
      onSubmit={handleSubmit}
      submitText="Save"
      onCancel={value && onClear ? handleClear : undefined}
      cancelText={value && onClear ? "Clear" : undefined}
    >
      <StyledDatePickerWrapper theme={theme}>
        {renderDatePicker()}
      </StyledDatePickerWrapper>
    </Modal>
  );
};

DateTimePickerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  value: PropTypes.number,
  format: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  timeIntervals: PropTypes.number,
  allowKeyboardInput: PropTypes.bool,
  timezone: PropTypes.string
};

export default DateTimePickerModal;
